import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const userRole = localStorage.getItem('userRole');
      let endpoint;

      if (userRole === 'user') {
        endpoint = '/api/users/profile';
      } else if (userRole === 'provider' || userRole === 'agent') {
        endpoint = '/api/providers/profile';
      }

      if (endpoint) {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`);
        setUser({ ...res.data.user || res.data.provider || res.data.agent, role: userRole });
      }
    } catch (error) {
      console.error('Error loading user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token, loadUser]);

  const login = useCallback((token, userData, role) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userRole', role);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setToken(token);
    setUser({ ...userData, role });
  }, []);

  const patchUser = useCallback((partial) => {
    setUser(u => (u ? { ...u, ...partial } : u));
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    patchUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
